import { useMemo } from "react";
import type { ChatMessage as ChatMessageType } from "@imonsite/shared";
import { cn } from "../../lib/utils";

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
}

/**
 * Parse the display text from message content.
 * Assistant messages may contain JSON (AIOutput format) that needs to be parsed.
 */
function parseDisplayText(content: string, isUser: boolean): string {
  if (isUser) return content;
  
  // Try to parse as JSON (AIOutput format)
  try {
    const parsed = JSON.parse(content);
    // Extract text from the AIOutput
    if (typeof parsed === "object" && parsed !== null) {
      if (parsed.text) return parsed.text;
      if (parsed.type === "spec") return "Got it! Updating your site details...";
    }
  } catch {
    // Not JSON, return as-is (already plain text)
  }
  
  return content;
}

export function ChatMessage({ message, isStreaming }: ChatMessageProps) {
  const isUser = message.role === "user";
  
  // Parse content to get display text
  const displayText = useMemo(
    () => parseDisplayText(message.content, isUser),
    [message.content, isUser]
  );

  // Don't render empty messages
  if (!displayText.trim()) return null;

  return (
    <div
      className={cn(
        "flex gap-3 max-w-[85%] animate-in fade-in slide-in-from-bottom-2 duration-300",
        isUser && "ml-auto flex-row-reverse"
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          "flex-shrink-0 size-9 rounded-full flex items-center justify-center text-lg",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-accent/20 text-accent-foreground"
        )}
      >
        {isUser ? (
          <svg
            className="size-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        ) : (
          <svg
            className="size-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
            />
          </svg>
        )}
      </div>

      {/* Message bubble */}
      <div
        className={cn(
          "rounded-2xl px-4 py-3 leading-relaxed",
          isUser
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-secondary text-secondary-foreground rounded-bl-md"
        )}
      >
        <p className="whitespace-pre-wrap">{displayText}</p>
        {isStreaming && (
          <span className="inline-block w-2 h-4 ml-0.5 bg-current animate-pulse rounded-sm" />
        )}
      </div>
    </div>
  );
}

